import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getTopicGaps, TopicGapDTO } from '../../../services/tutorQuizService';

interface Props {
  courseId: number;
}

export default function TutorTopicGapsPanel({ courseId }: Props) {
  const navigate = useNavigate();
  const [gaps, setGaps] = useState<TopicGapDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTopicGaps(courseId);
      setGaps(data);
    } catch {
      setError('Failed to load topics without quizzes.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (gaps.length === 0) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={2} color="success.main">
        <CheckCircleOutlineIcon />
        <Typography variant="body2">All topics have an active quiz.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1}>
        {gaps.length} topic{gaps.length !== 1 ? 's' : ''} without an active quiz:
      </Typography>
      <List dense disablePadding>
        {gaps.map(gap => (
          <ListItem
            key={gap.nodeId}
            disablePadding
            sx={{ py: 0.5 }}
            secondaryAction={
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/tutor/courses/${courseId}/topics/${gap.nodeId}`)}
              >
                Generate Quiz
              </Button>
            }
          >
            <ListItemText primary={gap.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
